import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';

export const AdmissionInfo = () => {
  const [formData, setFormData] = useState({
    studentEmail: '',
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    language: 'English',
    howDidYouFind: '',
    referredBy: '',
    paymentMethod: '',
    studentSignature: '',
    studentSignatureDate: '',
    parentSignature: '',
    parentSignatureDate: '',
    parentPhone: '',
    parentEmail: '',
    agreeTerms: false
  });

  const [files, setFiles] = useState({
    transcript: null,
    passport: null,
    paymentProof: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      setFiles({ ...files, [fileType]: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('student_email', formData.studentEmail);
      formDataToSend.append('first_name', formData.firstName);
      formDataToSend.append('middle_name', formData.middleName);
      formDataToSend.append('last_name', formData.lastName);
      formDataToSend.append('date_of_birth', formData.dateOfBirth);
      formDataToSend.append('gender', formData.gender);
      formDataToSend.append('language', formData.language);
      formDataToSend.append('how_did_you_find', formData.howDidYouFind);
      formDataToSend.append('referred_by', formData.referredBy);
      formDataToSend.append('payment_method', formData.paymentMethod);
      formDataToSend.append('student_signature', formData.studentSignature);
      formDataToSend.append('student_signature_date', formData.studentSignatureDate);
      formDataToSend.append('parent_signature', formData.parentSignature);
      formDataToSend.append('parent_signature_date', formData.parentSignatureDate);
      formDataToSend.append('parent_phone', formData.parentPhone);
      formDataToSend.append('parent_email', formData.parentEmail);
      formDataToSend.append('agree_terms', formData.agreeTerms.toString());

      if (files.transcript) {
        formDataToSend.append('transcript', files.transcript);
      }
      if (files.passport) {
        formDataToSend.append('passport', files.passport);
      }
      if (files.paymentProof) {
        formDataToSend.append('payment_proof', files.paymentProof);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admission`, {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus('success');
        alert(data.message);
        setFormData({
          studentEmail: '',
          firstName: '',
          middleName: '',
          lastName: '',
          dateOfBirth: '',
          gender: '',
          language: 'English',
          howDidYouFind: '',
          referredBy: '',
          paymentMethod: '',
          studentSignature: '',
          studentSignatureDate: '',
          parentSignature: '',
          parentSignatureDate: '',
          parentPhone: '',
          parentEmail: '',
          agreeTerms: false
        });
        setFiles({ transcript: null, passport: null, paymentProof: null });
      } else {
        setSubmitStatus('error');
        alert(data.message || 'An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
      alert('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="admission-info" className="py-24 bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold">Student Registration</span>
          <h2 className="text-5xl font-bold text-gray-900 mt-4 mb-4">Admission Information</h2>
          <p className="text-xl text-gray-600">Complete the form below to begin your OSSD journey with EduNexa</p>
          <p className="text-gray-600 mt-2">Documents will be sent to: <a href="mailto:info@edunexa.ca" className="text-blue-600 font-semibold">info@edunexa.ca</a></p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-200">
          {/* Student Information */}
          <div className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-200 pb-3">Student Information</h3>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2">Student's Email *</label>
                <input
                  type="email"
                  name="studentEmail"
                  value={formData.studentEmail}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  placeholder="student@example.com"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Legal First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Legal Middle Name</label>
                  <input
                    type="text"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Legal Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Date of Birth *</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Language Preferred for Study *</label>
                <select
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value="English">English</option>
                  <option value="French">French</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">How did you find out about us?</label>
                  <select
                    name="howDidYouFind"
                    value={formData.howDidYouFind}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Select Option</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Friends/Family">Friends/Family</option>
                    <option value="Internet Search">Internet Search</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Referred by (if any)</label>
                  <input
                    type="text"
                    name="referredBy"
                    value={formData.referredBy}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    placeholder="Name of referrer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Payment Method *</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value="">Select Payment Method</option>
                  <option value="Visa">Visa</option>
                  <option value="MasterCard">MasterCard</option>
                  <option value="American Express">American Express</option>
                  <option value="Debit Card">Debit Card</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
            </div>
          </div>

          {/* Document Upload */}
          <div className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-200 pb-3">Upload Documents</h3>
            
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-blue-400 transition-colors">
                <label className="cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <Upload className="w-10 h-10 text-blue-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Last Official School Transcripts *</p>
                      <p className="text-sm text-gray-600">Upload PDF, JPG, or PNG (Max 5MB)</p>
                      {files.transcript && <p className="text-sm text-green-600 mt-1">✓ {files.transcript.name}</p>}
                    </div>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'transcript')}
                    required
                    className="hidden"
                  />
                </label>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-blue-400 transition-colors">
                <label className="cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <Upload className="w-10 h-10 text-blue-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Passport *</p>
                      <p className="text-sm text-gray-600">Upload PDF, JPG, or PNG (Max 5MB)</p>
                      {files.passport && <p className="text-sm text-green-600 mt-1">✓ {files.passport.name}</p>}
                    </div>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'passport')}
                    required
                    className="hidden"
                  />
                </label>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-blue-400 transition-colors">
                <label className="cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <Upload className="w-10 h-10 text-blue-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Application Fee Payment Proof</p>
                      <p className="text-sm text-gray-600">Upload receipt (Optional)</p>
                      {files.paymentProof && <p className="text-sm text-green-600 mt-1">✓ {files.paymentProof.name}</p>}
                    </div>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'paymentProof')}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Parent/Guardian Information */}
          <div className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-200 pb-3">Parent/Guardian Information</h3>
            
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Parent/Guardian Phone *</label>
                  <input
                    type="tel"
                    name="parentPhone"
                    value={formData.parentPhone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    placeholder="+1 XXX XXX XXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Parent/Guardian Email *</label>
                  <input
                    type="email"
                    name="parentEmail"
                    value={formData.parentEmail}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    placeholder="parent@example.com"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Signatures */}
          <div className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-200 pb-3">Signatures & Agreement</h3>
            
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Student Signature (Full Name) *</label>
                  <input
                    type="text"
                    name="studentSignature"
                    value={formData.studentSignature}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    placeholder="Type your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date *</label>
                  <input
                    type="date"
                    name="studentSignatureDate"
                    value={formData.studentSignatureDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Parent/Guardian Signature (Full Name) *</label>
                  <input
                    type="text"
                    name="parentSignature"
                    value={formData.parentSignature}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    placeholder="Type full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date *</label>
                  <input
                    type="date"
                    name="parentSignatureDate"
                    value={formData.parentSignatureDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="mb-8 bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
            <h4 className="font-bold text-gray-900 mb-4">Agreement & Disclaimer</h4>
            <p className="text-sm text-gray-700 mb-4 leading-relaxed">
              This is a binding contract. By signing this form, I agree that all information provided is correct and true to the best of my knowledge.
            </p>
            
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={handleChange}
                required
                className="mt-1 w-5 h-5"
              />
              <span className="text-sm font-medium text-gray-900">
                I agree to the terms and conditions outlined above *
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || !formData.agreeTerms}
            data-testid="admission-submit-btn"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-lg font-semibold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSubmitting ? (
              <>Processing...</>
            ) : (
              <>
                <FileText className="w-5 h-5 mr-2" />
                Submit Application
              </>
            )}
          </button>

          <p className="text-sm text-gray-600 text-center mt-4">
            Documents will be sent to <span className="font-semibold">info@edunexa.ca</span>
          </p>
        </form>
      </div>
    </section>
  );
};